// Shared Meta JS-SDK loader for Embedded Signup surfaces (the public /whatsapp-connect
// page and the MyAccount WhatsApp tab). Loads sdk.js once and FB.init()s with the
// Teamio app id. Single-flight: concurrent callers share one load.
const META_SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js';
// TODO(tab batch): add a load timeout — a script that never fires load OR error (stalled
// request, or a foreign tag that failed before we attached listeners) strands the promise.

let loadPromise = null;
// window.FB is set the moment sdk.js parses — BEFORE FB.init() runs, and it stays set
// even if FB.init() throws. So window.FB alone is not proof of a usable SDK; a fast path
// keyed on it would hand callers a half-initialized FB after a failed init and report
// success. Only a completed FB.init() flips this.
let initialized = false;

export function loadMetaSdk() {
  if (window.FB && initialized) return Promise.resolve(window.FB);
  if (loadPromise) return loadPromise;
  loadPromise = new Promise((resolve, reject) => {
    const done = () => {
      // VERIFY post-Tech-Provider-approval: these FB.init args (appId/version) and the
      // entire Embedded Signup SDK contract are assumed from Meta's docs and have not
      // yet been exercised against a real signup run (no Tech Provider approval yet).
      // This is the one authoritative copy of this caveat — call sites point here.
      try {
        window.FB.init({
          appId: process.env.VUE_APP_META_APP_ID,
          autoLogAppEvents: true,
          xfbml: false,
          version: 'v21.0',
        });
        initialized = true;
        resolve(window.FB);
      } catch (err) {
        loadPromise = null; // allow retry after a failed init
        reject(err);
      }
    };
    const failed = (script) => () => {
      // Drop the dead tag so a retry takes the fresh-script path instead of adopting a
      // corpse that will never fire load again.
      if (script) script.remove();
      loadPromise = null; // allow retry after a network failure
      reject(new Error('Meta SDK failed to load'));
    };
    // sdk.js already parsed but never successfully init'ed (a previous FB.init threw, or
    // another surface injected the script without init'ing). Re-run init rather than
    // injecting a second tag — this is the retry safety-net the pre-extraction page had.
    if (window.FB) { done(); return; }
    const existing = document.querySelector(`script[src="${META_SDK_SRC}"]`);
    if (existing) {
      // A tag someone else injected that is still in flight: it can still fail, so wait
      // on BOTH outcomes. Listening only for 'load' leaves the promise pending forever
      // when that load errors, stranding the caller's spinner with no catch.
      existing.addEventListener('load', done);
      existing.addEventListener('error', failed(existing));
      return;
    }
    const script = document.createElement('script');
    script.src = META_SDK_SRC;
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.onload = done;
    script.onerror = failed(script);
    document.head.appendChild(script);
  });
  return loadPromise;
}
